import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Globe, Search, Heart, User, LogOut, Menu, X, Plus, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ThemeToggle } from './ThemeToggle';

export const Navbar = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { href: '/', label: 'Accueil', icon: Globe },
    { href: '/search', label: 'Rechercher', icon: Search },
  ];

  const authLinks = [
    { href: '/favorites', label: 'Favoris', icon: Heart },
    { href: '/meal-plan', label: 'Planifier', icon: Calendar },
    { href: '/recipes/new', label: 'Ajouter', icon: Plus },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="sticky top-0 z-50 bg-worldplate-cream/95 backdrop-blur-md border-b border-stone-200">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
            <img 
              src="/logo.png" 
              alt="Globe Gourmand" 
              className="h-20 md:h-24 w-auto group-hover:scale-105 transition-transform"
            />
            <span className="font-accent text-3xl md:text-4xl font-bold text-worldplate-deep">
              Globe Gourmand
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-2">
            {navLinks.map((link) => (
              <Link key={link.href} to={link.href}>
                <Button
                  variant={isActive(link.href) ? 'default' : 'ghost'}
                  className={`gap-2 ${isActive(link.href) ? 'bg-worldplate-primary hover:bg-worldplate-primary/90' : 'text-worldplate-muted hover:text-worldplate-text hover:bg-worldplate-subtle'}`}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  <link.icon className="w-4 h-4" />
                  {link.label}
                </Button>
              </Link>
            ))}

            {isAuthenticated && authLinks.map((link) => (
              <Link key={link.href} to={link.href}>
                <Button
                  variant={isActive(link.href) ? 'default' : 'ghost'}
                  className={`gap-2 ${isActive(link.href) ? 'bg-worldplate-primary hover:bg-worldplate-primary/90' : 'text-worldplate-muted hover:text-worldplate-text hover:bg-worldplate-subtle'}`}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  <link.icon className="w-4 h-4" />
                  {link.label}
                </Button>
              </Link>
            ))}
          </div>

          {/* Auth Section */}
          <div className="hidden md:flex items-center gap-2">
            <ThemeToggle />
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2 text-worldplate-muted hover:text-worldplate-text" data-testid="nav-user-menu">
                    <div className="w-8 h-8 bg-worldplate-secondary rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <span className="font-medium">{user?.username}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="flex items-center gap-2 cursor-pointer" data-testid="nav-profile">
                      <User className="w-4 h-4" />
                      Mon profil
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/favorites" className="flex items-center gap-2 cursor-pointer" data-testid="nav-my-favorites">
                      <Heart className="w-4 h-4" />
                      Mes favoris
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="flex items-center gap-2 cursor-pointer text-worldplate-error" data-testid="nav-logout">
                    <LogOut className="w-4 h-4" />
                    Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login">
                  <Button variant="ghost" className="text-worldplate-muted hover:text-worldplate-text" data-testid="nav-login">
                    Connexion
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-worldplate-primary hover:bg-worldplate-primary/90 text-white rounded-full px-6" data-testid="nav-register">
                    S'inscrire
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-1">
            <ThemeToggle compact />
            <button
              className="p-2 text-worldplate-muted hover:text-worldplate-text"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="nav-mobile-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-worldplate-cream border-t border-stone-200"
          >
            <div className="px-4 py-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg ${isActive(link.href) ? 'bg-worldplate-primary text-white' : 'text-worldplate-muted hover:bg-worldplate-subtle'}`}
                >
                  <link.icon className="w-5 h-5" />
                  {link.label}
                </Link>
              ))}

              {isAuthenticated && (
                <>
                  {authLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg ${isActive(link.href) ? 'bg-worldplate-primary text-white' : 'text-worldplate-muted hover:bg-worldplate-subtle'}`}
                    >
                      <link.icon className="w-5 h-5" />
                      {link.label}
                    </Link>
                  ))}
                  <Link
                    to="/profile"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-worldplate-muted hover:bg-worldplate-subtle"
                  >
                    <User className="w-5 h-5" />
                    Mon profil
                  </Link>
                  <button
                    onClick={() => { logout(); setMobileMenuOpen(false); }}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-worldplate-error hover:bg-red-50 w-full text-left"
                  >
                    <LogOut className="w-5 h-5" />
                    Déconnexion
                  </button>
                </>
              )}

              {!isAuthenticated && (
                <div className="pt-4 border-t border-stone-200 space-y-2">
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-4 py-3 text-center text-worldplate-muted hover:text-worldplate-text"
                  >
                    Connexion
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-4 py-3 text-center bg-worldplate-primary text-white rounded-lg"
                  >
                    S'inscrire
                  </Link>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
